/**
 * Le segnalazioni arrivate dai colleghi: lista di lavoro, non bacheca.
 * La vede solo l'admin — chi segnala non rilegge.
 */
import { useEffect, useState } from 'react';
import { segnalazioniApi } from '../api';

const fmtData = d => d
  ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit',
                                          hour: '2-digit', minute: '2-digit' })
  : '—';

const TIPI = [
  { v: '',         l: 'Tutte',     icona: '',   attivo: 'bg-gray-800' },
  { v: 'bug',      l: 'Problemi',  icona: '🐞', attivo: 'bg-red-600' },
  { v: 'proposta', l: 'Proposte',  icona: '💡', attivo: 'bg-blue-600' },
];
const STATI = [
  { v: '', l: 'Tutte' },
  { v: 'nuova', l: 'Nuove' },
  { v: 'presa_in_carico', l: 'In corso' },
  { v: 'risolta', l: 'Risolte' },
  { v: 'scartata', l: 'Scartate' },
];
const STILE = {
  nuova:           'bg-amber-100 text-amber-800',
  presa_in_carico: 'bg-blue-100 text-blue-700',
  risolta:         'bg-green-100 text-green-700',
  scartata:        'bg-gray-100 text-gray-500',
};
const ETICHETTA = { nuova: 'nuova', presa_in_carico: 'in corso', risolta: 'risolta', scartata: 'scartata' };

export default function Segnalazioni() {
  const [dati, setDati] = useState({ righe: [], conteggi: {}, per_tipo: {}, aperte_per_tipo: {} });
  const [filtro, setFiltro] = useState('');
  const [tipo, setTipo] = useState('');
  const [note, setNote] = useState({});
  // Vista compatta per scorrerle tutte, estesa per lavorarci sopra.
  const [compatta, setCompatta] = useState(false);

  const load = () => segnalazioniApi.list({
      ...(filtro ? { stato: filtro } : {}),
      ...(tipo ? { tipo } : {}),
    }).then(d => { setDati(d); setNote({}); }).catch(console.error);
  useEffect(() => { load(); }, [filtro, tipo]);

  const cambia = async (s, stato) => {
    try { await segnalazioniApi.aggiorna(s.id, { stato, note_admin: note[s.id] ?? s.note_admin }); load(); }
    catch (e) { alert(e); }
  };
  const elimina = async (s) => {
    if (!confirm(`Eliminare definitivamente la segnalazione "${s.titolo}"?`)) return;
    try { await segnalazioniApi.elimina(s.id); load(); } catch (e) { alert(e); }
  };

  // Esportazione leggibile: serve per guardarle insieme senza stare davanti
  // allo schermo del gestionale. Testo semplice, apribile ovunque.
  const esporta = () => {
    const r = dati.righe || [];
    const et = { nuova: 'NUOVA', presa_in_carico: 'IN CORSO', risolta: 'RISOLTA', scartata: 'SCARTATA' };
    const testo = [
      'SEGNALAZIONI — GESTIONALE SINTHEMA',
      `Estratto il ${new Date().toLocaleString('it-IT')}`,
      `${r.length} segnalazioni` + (tipo ? ` · solo ${tipo === 'bug' ? 'problemi' : 'proposte'}` : '')
        + (filtro ? ` · stato: ${filtro.replace('_', ' ')}` : ''),
      '='.repeat(70), '',
      ...r.map((x, i) => [
        `${i + 1}. [${x.tipo === 'bug' ? 'PROBLEMA' : 'PROPOSTA'}] [${et[x.stato]}] ${x.titolo}`,
        `   da ${x.autore || 'utente eliminato'}${x.autore_ruolo === 'esterno' ? ' (esterno)' : ''}` +
          ` il ${fmtData(x.created_at)}`,
        x.descrizione ? '   ' + x.descrizione.replace(/\n/g, '\n   ') : null,
        `   schermata ${x.pagina || '—'} · versione ${x.versione || '—'} · ${x.browser || '—'}`,
        x.note_admin ? `   NOTA: ${x.note_admin}` : null,
        '',
      ].filter(Boolean).join('\n')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([testo], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `segnalazioni-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const righe = dati.righe || [];
  const c = dati.conteggi || {};
  const totale = Object.values(dati.per_tipo || {}).reduce((a, b) => a + b, 0);
  const totAperte = Object.values(dati.aperte_per_tipo || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-display font-semibold text-gray-900">Segnalazioni</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Problemi e proposte inviati dai colleghi. Schermata, versione e browser sono
          allegati in automatico.
        </p>
      </div>

      {/* Riepilogo: quanti problemi e quante proposte restano aperti. È il
          numero che conta — il totale storico dice poco. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          ['🐞 Problemi aperti', dati.aperte_per_tipo?.bug || 0, 'text-red-600'],
          ['💡 Proposte aperte', dati.aperte_per_tipo?.proposta || 0, 'text-blue-600'],
          ['✓ Risolte', c.risolta || 0, 'text-green-700'],
          ['Totale ricevute', totale, 'text-gray-700'],
        ].map(([l, v, col]) => (
          <div key={l} className="card py-3">
            <div className="text-xs text-gray-500">{l}</div>
            <div className={`text-2xl font-bold mt-0.5 ${col}`}>{v}</div>
          </div>
        ))}
      </div>

      {/* Le due domande sono diverse: "cosa è rotto" e "cosa mi hanno chiesto".
          Tenerle su righe separate evita di doverle incrociare a mente. */}
      <div className="space-y-2">
        <div className="flex gap-1.5 flex-wrap items-center">
          {TIPI.map(t => {
            const attivo = tipo === t.v;
            const aperte = t.v ? (dati.aperte_per_tipo?.[t.v] || 0) : totAperte;
            const tot = t.v ? (dati.per_tipo?.[t.v] || 0) : totale;
            return (
              <button key={t.v} onClick={() => setTipo(t.v)}
                className={`btn-sm btn gap-2 ${attivo
                  ? `${t.attivo} text-white`
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}>
                <span>{t.icona} {t.l}</span>
                <span className={`text-[10px] px-1.5 rounded-full ${attivo
                  ? 'bg-white/25' : aperte ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'}`}>
                  {aperte ? `${aperte} da fare` : `${tot}`}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex gap-1 flex-wrap items-center">
          {STATI.map(s => (
            <button key={s.v} onClick={() => setFiltro(s.v)}
              className={`btn-sm btn ${filtro === s.v
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
              {s.l}{s.v && c[s.v] ? ` (${c[s.v]})` : ''}
            </button>
          ))}
          <div className="ml-auto flex gap-1.5">
            <button className="btn-secondary btn-sm" onClick={() => setCompatta(v => !v)}>
              {compatta ? '☰ Vista estesa' : '≡ Vista compatta'}
            </button>
            <button className="btn-secondary btn-sm" disabled={!righe.length} onClick={esporta}
                    title="Scarica l'elenco filtrato come file di testo">
              ↓ Esporta
            </button>
          </div>
        </div>
      </div>

      {righe.length === 0 && (
        <div className="card text-center text-gray-400 py-10">
          {tipo === 'bug' ? 'Nessun problema segnalato'
            : tipo === 'proposta' ? 'Nessuna proposta'
            : 'Nessuna segnalazione'}
          {filtro && ' con questo stato'}
        </div>
      )}

      <div className={compatta ? 'space-y-1.5' : 'space-y-3'}>
        {righe.map(s => (
          <div key={s.id}
               className={`card border-l-4 ${compatta ? 'py-2.5' : ''} ${s.tipo === 'bug' ? 'border-l-red-400' : 'border-l-blue-400'}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Etichetta esplicita, non solo l'emoji: a colpo d'occhio
                      "problema" e "proposta" richiedono reazioni diverse. */}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.tipo === 'bug'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                    {s.tipo === 'bug' ? '🐞 Problema' : '💡 Proposta'}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STILE[s.stato]}`}>
                    {ETICHETTA[s.stato]}
                  </span>
                  <span className="text-xs text-gray-400">
                    {s.autore || 'utente eliminato'}
                    {s.autore_ruolo === 'esterno' && ' · esterno'} · {fmtData(s.created_at)}
                  </span>
                </div>
                <div className="font-medium text-gray-900 mt-1">{s.titolo}</div>
                {!compatta && s.descrizione && (
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{s.descrizione}</p>
                )}
                {/* Il contesto tecnico: è ciò che rende la segnalazione diagnosticabile */}
                {!compatta && (
                  <div className="flex gap-3 text-xs text-gray-400 mt-2 flex-wrap font-mono">
                    {s.pagina && <span>{s.pagina}</span>}
                    {s.versione && <span>v{s.versione}</span>}
                    {s.browser && <span>{s.browser}</span>}
                  </div>
                )}
                {compatta && s.note_admin && (
                  <div className="text-xs text-gray-500 mt-0.5 truncate">NOTA: {s.note_admin}</div>
                )}
              </div>
              <button className="text-gray-300 hover:text-red-600 text-sm shrink-0"
                      title="Elimina" onClick={() => elimina(s)}>🗑</button>
            </div>

            <div className={`mt-3 pt-3 border-t items-end gap-2 flex-wrap ${compatta ? 'hidden' : 'flex'}`}>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs text-gray-500 mb-0.5">Note</label>
                <input className="input py-1 text-sm"
                       placeholder="Cosa hai deciso, o cosa hai corretto"
                       value={note[s.id] ?? s.note_admin ?? ''}
                       onChange={e => setNote(n => ({ ...n, [s.id]: e.target.value }))} />
              </div>
              {s.stato !== 'presa_in_carico' && (
                <button className="btn-secondary btn-sm" onClick={() => cambia(s, 'presa_in_carico')}>
                  Prendi in carico
                </button>
              )}
              {s.stato !== 'risolta' && (
                <button className="btn-primary btn-sm" onClick={() => cambia(s, 'risolta')}>
                  ✓ Risolta
                </button>
              )}
              {s.stato !== 'scartata' && (
                <button className="btn-secondary btn-sm" onClick={() => cambia(s, 'scartata')}>
                  Scarta
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
